import { useTranslate, useMany } from '@refinedev/core';
import { List, DeleteButton } from '@refinedev/antd';
import { Table, Space } from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import type { FCMTokenOut, UserOut } from '@/api/generated/schemas';
import type { CrudFilter } from '@refinedev/core';
import { useAdminTable } from '@/hooks/useAdminTable';
import { SelectFilter } from '@/components/common/SelectFilter';
import { TextFilter } from '@/components/common/TextFilter';
import { DateRangeFilter } from '@/components/common/DateRangeFilter';
import { isFieldFiltered } from '@/utils/filters';
import { getSortOrder } from '@/utils/sorters';
import { useTableRowNavigateToShow } from '@/hooks/useTableRowNavigateToShow';

const PLATFORM_OPTIONS = ['android', 'ios', 'web'];

export const FCMTokenList = () => {
  const t = useTranslate();
  const rowNav = useTableRowNavigateToShow();

  const { tableProps, setFilters, filters, sorters } =
    useAdminTable<FCMTokenOut>({
      pagination: { pageSize: 20 },
      resource: 'account.fcm_tokens',
      sorters: {
        initial: [{ field: 'created_at', order: 'desc' }],
      },
    });

  const tokenData = (tableProps.dataSource ?? []) as FCMTokenOut[];
  const userIds = [...new Set(tokenData.map((t) => t.user_id))];

  const { result: usersResult } = useMany<UserOut>({
    resource: 'iam.users',
    ids: userIds,
    queryOptions: { enabled: userIds.length > 0 },
  });

  const usersById = Object.fromEntries(
    (usersResult?.data ?? []).map((u: UserOut) => [u.id, u])
  );

  return (
    <List canCreate={false}>
      <Table {...rowNav('account.fcm_tokens', tableProps)} rowKey="id">
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
        />

        <Table.Column
          dataIndex="user_id"
          title={t('account.fcm_tokens.fields.user_id')}
          sorter
          sortOrder={getSortOrder(sorters, 'user_id')}
          render={(userId: number) => {
            const user = usersById[userId];
            return (
              <Space size={8}>
                <span>{user ? (user.email ?? user.username) : userId}</span>
                <Link to={`/iam/users/${userId}`}>
                  <LinkOutlined />
                </Link>
              </Space>
            );
          }}
          filtered={isFieldFiltered(filters as CrudFilter[], 'user_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <TextFilter
              field="user_id"
              placeholder={t('account.fcm_tokens.fields.user_id')}
              width={120}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="platform"
          title={t('account.fcm_tokens.fields.platform')}
          sorter
          sortOrder={getSortOrder(sorters, 'platform')}
          render={(value: string | null) => value ?? '-'}
          filtered={isFieldFiltered(filters as CrudFilter[], 'platform')}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter
              field="platform"
              minWidth={120}
              placeholder={t('account.fcm_tokens.fields.platform')}
              options={PLATFORM_OPTIONS.map((v) => ({
                label: t(`account.fcm_tokens.platform.${v}`),
                value: v,
              }))}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="token"
          title={t('account.fcm_tokens.fields.token')}
          render={(value: string) => (value ? `${value.slice(0, 20)}…` : '-')}
        />

        <Table.Column
          dataIndex="app_version"
          title={t('account.fcm_tokens.fields.app_version')}
          render={(value: string | null) => value ?? '-'}
        />

        <Table.Column
          dataIndex="ip_address"
          title={t('account.fcm_tokens.fields.ip_address')}
          render={(value: string | null) => value ?? '-'}
        />

        <Table.Column
          dataIndex="created_at"
          title={t('base.fields.created_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'created_at')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'created_at')}
          filterDropdown={({ confirm, clearFilters }) => (
            <DateRangeFilter
              field="created_at"
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: string) =>
            value ? new Date(value).toLocaleString() : '-'
          }
        />

        <Table.Column
          title={t('base.fields.actions')}
          render={(_: unknown, record: FCMTokenOut) => (
            <DeleteButton
              resource="account.fcm_tokens"
              recordItemId={record.id}
              hideText
              size="small"
            />
          )}
        />
      </Table>
    </List>
  );
};
